import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { validatorHexColor } from '@emma-helpers/emma-validators.helper';
import { isHexColor } from '@emma-helpers/is-function.helper';
import { noop } from 'lodash';
import { takeUntil } from 'rxjs/operators';

import { EMMAFormElementComponent } from './emma-form-element.component';

const EMMA_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMAInputColorComponent),
  multi: true,
};

const EMMA_INPUT_VALIDATOR: any = {
  provide: NG_VALIDATORS,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMAInputColorComponent),
  multi: true,
};

@Component({
    selector: 'emma-input-color',
    templateUrl: './emma-input-color.component.html',
    providers: [
        EMMA_INPUT_CONTROL_VALUE_ACCESSOR,
        EMMA_INPUT_VALIDATOR,
        {
            provide: EMMAFormElementComponent,
            useExisting: EMMAInputColorComponent,
        },
    ],
    standalone: false
})
export class EMMAInputColorComponent
  extends EMMAFormElementComponent
  implements OnInit, OnChanges, ControlValueAccessor, Validator
{
  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: string | number) => void = noop;

  public _value = '';

  @Input()
  get value() {
    return this._value ? `#${this._value}` : this._value;
  }
  set value(v: string) {
    if (this._value !== v) {
      this.updateValue(v);
      this.onChangeCallback(this.value);
      this.valueChange.emit(this.value);
    }
  }

  @Output() valueChange = new EventEmitter<string>();

  isHexColor = isHexColor;

  override ngOnInit(): void {
    this.updateClasses();
  }
  override ngOnChanges(changes: SimpleChanges): void {
    this.updateClasses();
  }
  override updateClasses() {
    this.$change.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event) => {
      this.onChangeCallback(event);
    });
    this.$blur.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event) => {
      this.onTouchedCallback();
    });
  }

  updateValue(v: string) {
    this._value = v ? v.replace(/#/g, '') : v;
  }

  // From ControlValueAccessor interface
  writeValue(value: string) {
    this.updateValue(value);
  }

  // From ControlValueAccessor interface
  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  // From ControlValueAccessor interface
  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  // From Validator interface
  override validate: ValidatorFn = (control: AbstractControl) => {
    const validators: ValidatorFn[] = [];
    if (this.required) {
      validators.push(Validators.required);
    }
    validators.push(validatorHexColor);
    if (this.validateFn) {
      validators.push(this.validateFn);
    }
    if (validators.length) {
      const validator = Validators.compose(validators);
      return validator ? validator(control) : null;
    }
    return null;
  };
}
